import type { CampaignResponse } from '@/api/campaignPlaner/fetchCampaign';
import { getCampaignInternalRules } from '@/dashboards/CampaignOptimization/helpers/getCampaignInternalRules';
import type { DashboardRule } from '@/optimizer';

export const CAMPAIGN_DASHBOARD_TYPE = 'campaign' as const;

const isPercentActiveDaysRule = (rule: DashboardRule) =>
    rule.type === 'Set active days to' && rule.subtype === '% of current days';

const activeDaysStateRules = (campaign: CampaignResponse): DashboardRule[] =>
    (campaign.campaignStateRules ?? []).filter(isPercentActiveDaysRule).map(
        ({ originalValue: _originalValue, isScalable: _isScalable, isCampaignVariable: _isCampaignVariable, calculatedCampaignValue, ...rule }) =>
            calculatedCampaignValue
                ? { ...rule, subtype: 'days', value: calculatedCampaignValue, target: CAMPAIGN_DASHBOARD_TYPE }
                : { ...rule, target: CAMPAIGN_DASHBOARD_TYPE },
    );

export const buildOptimisationRules = ({
    configRules,
    campaign,
    startDate,
    endDate,
    lockedChannels,
}: {
    configRules: DashboardRule[];
    campaign: CampaignResponse;
    startDate: string;
    endDate: string;
    lockedChannels: string[];
}): DashboardRule[] => {
    // A lock is sent as an "is exactly" rule and applied first; a later minimum/maximum rule for the
    // same channel replaces its lower/upper limit, so lower can exceed upper and core_optimizer's
    // _get_bounds raises. Dropping the channel's budget rules from every source mirrors what the
    // optimiser's own IsExactlyRule conflict resolution would do if the lock were added last.
    const overriddenByLock = (rule: DashboardRule) => rule.type === 'Total budget' && lockedChannels.includes(rule.channel);

    const internal = getCampaignInternalRules(campaign, startDate, endDate)
        .filter((rule) => !overriddenByLock(rule))
        .filter((rule) => !isPercentActiveDaysRule(rule))
        .map((rule) => ({ ...rule, target: CAMPAIGN_DASHBOARD_TYPE }));

    return [...configRules.filter((rule) => !overriddenByLock(rule)), ...internal, ...activeDaysStateRules(campaign)];
};
